import React from 'react';
import { useSelector } from 'react-redux';
import {
    ActionButton,
    Divider,
    Flex,
    Grid,
    Item,
    Menu,
    MenuTrigger,
    repeat,
    Section,
    Text,
} from '@adobe/react-spectrum';

import { BaseDeckPane } from './BaseDeckPane';
import { Bar, BarChart, Legend, XAxis, YAxis, ResponsiveContainer, AreaChart, CartesianGrid, Area } from 'recharts';
import { getGrades } from '../../../data/ScoreDisplayUtils';
import styles from './manabasePane.css';
import './mana-cost.css';
import { DetailsExpansionSubpane } from './DetailsExpansionSubpane';

export function ManabasePane() {
    const deck = useSelector((state) => state.deck.deck);
    const cards = useSelector((state) => state.deck.cards);
    const allowBetaFeatures = useSelector((state) => state.app.allowBetaFeatures);

    const CATEGORY_CONSTANTS = {
        ritualScores: {
            color: '#757474',
            label: 'Rituals',
            chartLabel: 'Rituals',
        },
        dorkScores: {
            color: '#36b036',
            label: 'Dorks',
            chartLabel: 'Dorks',
        },
        rockScores: {
            color: '#cebbbb',
            label: 'Rocks',
            chartLabel: 'Rocks',
        },
        landScores: {
            color: '#ad6133',
            label: 'Lands',
            chartLabel: 'Lands',
        },
        treasureScores: {
            color: '#ad6133',
            label: 'Treasure',
            chartLabel: 'Treasure',
        },
    };

    const GRAPH_CONSTANTS = {
        c: {
            color: '#757474',
            label: 'Colorless',
            chartLabel: 'Colorless',
        },
        w: {
            color: '#ffffff',
            label: 'White',
            chartLabel: 'White',
        },
        u: {
            color: '#0000FF',
            label: 'Blue',
            chartLabel: 'Blue',
        },
        b: {
            color: '#000000',
            label: 'Black',
            chartLabel: 'Black',
        },
        r: {
            color: '#FF0000',
            label: 'Red',
            chartLabel: 'Red',
        },
        g: {
            color: '#00FF00',
            label: 'Green',
            chartLabel: 'Green',
        },
    };

    const getCategories = () => {
        let list = {};

        const categoryKeys = Object.keys(CATEGORY_CONSTANTS).sort((a, b) => {
            if (CATEGORY_CONSTANTS[a].label.toLowerCase() < CATEGORY_CONSTANTS[b].label.toLowerCase()) {
                return -1;
            }
            if (CATEGORY_CONSTANTS[a].label.toLowerCase() > CATEGORY_CONSTANTS[b].label.toLowerCase()) {
                return 1;
            }
            return 0;
        });

        categoryKeys.map((category) => {
            if (
                deck?.details?.manabase?.breakdown?.hasOwnProperty(category) &&
                Object.keys(deck?.details?.manabase?.breakdown?.[category])?.length > 0
            ) {
                const item = deck?.details?.manabase?.breakdown?.[category];
                let items = Object.keys(item).map((id) => {
                    return {
                        score: parseFloat(item?.[id] || '0.00').toFixed(2),
                        ...cards?.[id],
                    };
                });

                list[category] = {
                    label: `${CATEGORY_CONSTANTS[category]?.label}`,
                    id: category,
                    count: items?.length || 0,
                    list: items,
                    color: CATEGORY_CONSTANTS?.[category]?.color || `#FFFFFF`,
                    subCategories: [],
                    chartLabel: `${CATEGORY_CONSTANTS[category]?.chartLabel}`,
                };
            }
        });

        return list;
    };

    const data = Object.keys(deck?.details?.manabase?.breakdown?.production?.breakdown)?.map((color) => {
        const _a = Math.ceil(
            parseFloat(deck?.details?.manabase?.breakdown?.production?.breakdown?.[color]?.percentages?.requirements) *
                100
        );
        const _b = Math.ceil(
            parseFloat(
                // ?.productionToRequirementRatio
                deck?.details?.manabase?.breakdown?.production?.breakdown?.[color]?.percentages?.production
            ) * 100
        );

        return {
            name: GRAPH_CONSTANTS?.[color]?.label,
            category: GRAPH_CONSTANTS?.[color]?.label,
            Production: _b,
            Requirements: _a,
        };
    });

    const cardIds = Object.keys(deck?.details?.manabase?.probabilities?.cards || []);
    const cardIdList = () => {
        const permanentTypes = [
            'lands',
            'commanders',
            'planeswalkers',
            'creatures',
            'sorceries',
            'instants',
            'artifacts',
            'enchantments',
            'battles',
        ];

        const commanderCards = [];
        const creatures = [];
        const planeswalkers = [];
        const sorceries = [];
        const instants = [];
        const artifacts = [];
        const enchantments = [];
        const lands = [];
        const battles = [];

        const assignBucket = (id) => {
            let matched = false;
            permanentTypes.forEach((type) => {
                switch (type) {
                    case 'commanders':
                        deck?.commanderIds?.forEach((commanderId) => {
                            if (id === commanderId && !commanderCards.includes(commanderId)) {
                                commanderCards.push(commanderId);
                            }
                        });
                        break;
                    case 'planeswalkers':
                        if (cards?.[id]?.types?.toString()?.includes('planeswalker') && !matched) {
                            planeswalkers.push(id);
                            matched = true;
                        }
                        break;
                    case 'creatures':
                        if (cards?.[id]?.types?.toString()?.includes('creature') && !matched) {
                            creatures.push(id);
                            matched = true;
                        }
                        break;
                    case 'sorceries':
                        if (cards?.[id]?.types?.toString()?.includes('sorcery') && !matched) {
                            sorceries.push(id);
                            matched = true;
                        }
                        break;
                    case 'instants':
                        if (cards?.[id]?.types?.toString()?.includes('instant') && !matched) {
                            instants.push(id);
                            matched = true;
                        }
                        break;
                    case 'artifacts':
                        if (cards?.[id]?.types?.toString()?.includes('artifact') && !matched) {
                            artifacts.push(id);
                            matched = true;
                        }
                        break;
                    case 'enchantments':
                        if (cards?.[id]?.types?.toString()?.includes('enchantment') && !matched) {
                            enchantments.push(id);
                            matched = true;
                        }
                        break;
                    // case 'lands':
                    //     if (cards?.[id]?.types?.toString()?.includes('land') && !matched) {
                    //         lands.push(id);
                    //         matched = true;
                    //     }
                    //     break;
                    case 'battles':
                        if (cards?.[id]?.types?.toString()?.includes('battle') && !matched) {
                            battles.push(id);
                            matched = true;
                        }
                        break;
                }
            });
        };

        Object.keys(deck?.cards || [])?.forEach((id) => {
            assignBucket(id);
        });

        const list = [];

        if (commanderCards?.length > 0) {
            list.push({
                name: `Commander${commanderCards?.length > 1 ? 's' : ''}`,
                id: `commanders`,
                children: commanderCards
                    .map((id) => {
                        return {
                            id,
                            name: cards?.[id]?.name,
                        };
                    })
                    ?.sort((a, b) => {
                        if (a.name < b.name) {
                            return -1;
                        }
                        if (a.name > b.name) {
                            return 1;
                        }
                    }),
            });
        }

        if (planeswalkers?.length > 0) {
            list.push({
                name: `Planeswalkers`,
                id: `planeswalkerCards`,
                children: planeswalkers
                    .map((id) => {
                        return {
                            id,
                            name: cards?.[id]?.name,
                        };
                    })
                    ?.sort((a, b) => {
                        if (a.name < b.name) {
                            return -1;
                        }
                        if (a.name > b.name) {
                            return 1;
                        }
                    }),
            });
        }

        if (creatures?.length > 0) {
            list.push({
                name: `Creatures`,
                id: `creatureCards`,
                children: creatures
                    .map((id) => {
                        return {
                            id,
                            name: cards?.[id]?.name,
                        };
                    })
                    ?.sort((a, b) => {
                        if (a.name < b.name) {
                            return -1;
                        }
                        if (a.name > b.name) {
                            return 1;
                        }
                    }),
            });
        }

        if (sorceries?.length > 0) {
            list.push({
                name: `Sorceries`,
                id: `sorceryCards`,
                children: sorceries
                    .map((id) => {
                        return {
                            id,
                            name: cards?.[id]?.name,
                        };
                    })
                    ?.sort((a, b) => {
                        if (a.name < b.name) {
                            return -1;
                        }
                        if (a.name > b.name) {
                            return 1;
                        }
                    }),
            });
        }

        if (instants?.length > 0) {
            list.push({
                name: `Instants`,
                id: `instantCards`,
                children: instants
                    .map((id) => {
                        return {
                            id,
                            name: cards?.[id]?.name,
                        };
                    })
                    ?.sort((a, b) => {
                        if (a.name < b.name) {
                            return -1;
                        }
                        if (a.name > b.name) {
                            return 1;
                        }
                    }),
            });
        }

        if (artifacts?.length > 0) {
            list.push({
                name: `Artifacts`,
                id: `artifactCards`,
                children: artifacts
                    .map((id) => {
                        return {
                            id,
                            name: cards?.[id]?.name,
                        };
                    })
                    ?.sort((a, b) => {
                        if (a.name < b.name) {
                            return -1;
                        }
                        if (a.name > b.name) {
                            return 1;
                        }
                    }),
            });
        }

        if (enchantments?.length > 0) {
            list.push({
                name: `Enchantments`,
                id: `enchantmentCards`,
                children: enchantments
                    .map((id) => {
                        return {
                            id,
                            name: cards?.[id]?.name,
                        };
                    })
                    ?.sort((a, b) => {
                        if (a.name < b.name) {
                            return -1;
                        }
                        if (a.name > b.name) {
                            return 1;
                        }
                    }),
            });
        }

        if (lands?.length > 0) {
            list.push({
                name: `Lands`,
                id: `landCards`,
                children: lands
                    .map((id) => {
                        return {
                            id,
                            name: cards?.[id]?.name,
                        };
                    })
                    ?.sort((a, b) => {
                        if (a.name < b.name) {
                            return -1;
                        }
                        if (a.name > b.name) {
                            return 1;
                        }
                    }),
            });
        }

        if (battles?.length > 0) {
            list.push({
                name: `Battles`,
                id: `battleCards`,
                children: battles
                    .map((id) => {
                        return {
                            id,
                            name: cards?.[id]?.name,
                        };
                    })
                    ?.sort((a, b) => {
                        if (a.name < b.name) {
                            return -1;
                        }
                        if (a.name > b.name) {
                            return 1;
                        }
                    }),
            });
        }

        // console.log(JSON.stringify(list, null, 4));
        return list;
    };
    // const cardData = Object.keys(deck?.details?.manabase?.probabilities?.cards || [])?.map((cardId) => {

    const cardData = [];
    // const cardData = () => {
    //     const list = [];

    const dropDownMenuData = cardIds.map((id) => {
        return {
            name: deck?.cards[id].name,
            id,
        };
    });

    const [dropDownCardId, setDropDownCardId] = React.useState(cardIdList()?.[0]?.children?.[0]?.id || null);

    if (cardIds && cardIds.length > 0) {
        for (let i = 1; i < 9; i++) {
            // const card = deck.details.manabase.probabilities.cards[cardIds[0]];
            const card = deck.details?.manabase?.probabilities?.cards?.[dropDownCardId];
            // const _a = Math.ceil(
            //     parseFloat(
            //         card.
            //     ) * 100
            // );
            // const wtf = card.turns[i];
            const inHand = Math.ceil(
                parseFloat(
                    // ?.productionToRequirementRatio
                    card?.turns?.[i].inHand
                ) * 100
            );

            // return {
            //     name: GRAPH_CONSTANTS?.[color]?.label,
            //     category: GRAPH_CONSTANTS?.[color]?.label,
            //     Production: _b,
            //     Requirements: _a,
            // };
            cardData.push({
                name: `Turn ${i}`,
                label: 'Percent Chance',
                inHand,
            });
        }
    }

    const curveData = [];

    for (let i = 1; i < 9; i++) {
        // const card = deck.details.manabase.probabilities.cards[cardIds[0]];
        const curveObject = deck?.details?.manabase?.curveChart?.turns?.[i];
        const baseline = Math.ceil(
            parseFloat(
                // ?.productionToRequirementRatio
                curveObject?.baseLinePercentage
            ) * 100
        );

        const actual = Math.ceil(
            parseFloat(
                // ?.productionToRequirementRatio
                curveObject?.actualPercentage
            ) * 100
        );

        // return {
        //     name: GRAPH_CONSTANTS?.[color]?.label,
        //     category: GRAPH_CONSTANTS?.[color]?.label,
        //     Production: _b,
        //     Requirements: _a,
        // };
        curveData.push({
            name: `Turn ${i}`,
            'Expected Percentage': baseline,
            'Actual Percentage': actual,
            outlineColor: `#8884d8`,
        });
    }

    const openingHandData = [];

    const getColorLineBySymbol = (symbol) => {
        switch (symbol) {
            case 'w':
                return `#FFFFFF`;
                break;
            case 'u':
                return `#009dff`;
                break;
            case 'b':
                return `#4800d1`;
                break;
            case 'r':
                return `#ff0000`;
                break;
            case 'g':
                return `#209d00`;
                break;
            case 'c':
                return `#a1adb3`;
                break;
        }

        return `#8884d8`;
    };

    const handObject = deck?.details?.manabase?.probabilities?.openingHand;

    for (let i = 0; i < 8; i++) {
        const dataObj = {
            name: `${i}`,
        };
        const colors = Object.keys(handObject?.color || {});

        // const card = deck.details.manabase.probabilities.cards[cardIds[0]];
        colors.forEach((color) => {
            dataObj[color] = handObject.color[color][i] * 100;
        });

        dataObj['fastmana'] = handObject?.fastmana?.[i] * 100;

        openingHandData.push(dataObj);
    }

    console.log(JSON.stringify(openingHandData, null, 4));

    // return list;
    // };
    // });

    const handleBarClick = (data, index) => {};

    return (
        <BaseDeckPane
            title={'Manabase'}
            fieldName="name"
            content={<div />}
            showScore={true}
            categories={getCategories()}
        >
            {/*<linearGradient id="Gradient1">*/}
            {/*    <stop id="stop1" offset="0%" />*/}
            {/*    <stop id="stop2" offset="50%" />*/}
            {/*    <stop id="stop3" offset="100%" />*/}
            {/*</linearGradient>*/}
            <Flex direction="column">
                <Flex direction="row" justifyContent="center" marginTop="10px">
                    <Flex direction="column" justifyContent="center" marginEnd="10px" marginStart={10} width={'100%'}>
                        <span className="panelText" style={{ fontSize: '15px', fontWeight: 'bolder' }}>
                            <p />
                            How well does your mana production meet your card requirements?
                        </span>

                        <Divider width="100%" marginTop="10px" size={'S'} />
                        {allowBetaFeatures && deck?.details?.manabase?.probabilities && (
                            <>
                                <Text UNSAFE_className={'manabaseHeaderLabel'} marginTop={30}>
                                    Percentage for quantities in opening hand (7 cards)
                                </Text>
                                <Flex direction="row" justifyContent={'space-around'} marginTop={30}>
                                    <div
                                        style={{
                                            position: 'relative',
                                            width: '100%',
                                            maxWidth: '700px',
                                            paddingBottom: '250px',
                                            height: 200,
                                        }}
                                    >
                                        <div
                                            style={{
                                                position: 'absolute',
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                top: 0,
                                            }}
                                        >
                                            <ResponsiveContainer width="100%" height={400}>
                                                <AreaChart
                                                    // layout="vertical"
                                                    width={400}
                                                    height={400}
                                                    data={openingHandData}
                                                    // barGap={1}
                                                    // tick
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="name" />
                                                    <YAxis />
                                                    <Legend />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="fastmana"
                                                        // stackId="1"
                                                        stroke="#8884d8"
                                                        strokeWidth={3}
                                                        fillOpacity={0}
                                                        fill="#000000"
                                                    />

                                                    {Object.keys(handObject.color).map((color) => {
                                                        console.log(`fart=> ${color}`);
                                                        return (
                                                            <Area
                                                                type="monotone"
                                                                dataKey={color}
                                                                // stackId="1"
                                                                stroke={getColorLineBySymbol(color)}
                                                                strokeWidth={3}
                                                                fillOpacity={0}
                                                                fill="#000000"
                                                            />
                                                        );
                                                    })}
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                </Flex>

                                <Text UNSAFE_className={'manabaseHeaderLabel'} marginTop={30}>
                                    Percentage of cards in deck castable by turn
                                </Text>
                                <Flex direction="row" justifyContent={'space-around'} marginTop={30}>
                                    <div
                                        style={{
                                            position: 'relative',
                                            width: '100%',
                                            maxWidth: '700px',
                                            paddingBottom: '250px',
                                            height: 200,
                                        }}
                                    >
                                        <div
                                            style={{
                                                position: 'absolute',
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                top: 0,
                                            }}
                                        >
                                            <ResponsiveContainer width="100%" height={400}>
                                                <AreaChart
                                                    // layout="vertical"
                                                    width={400}
                                                    height={400}
                                                    data={curveData}
                                                    // barGap={1}
                                                    // tick
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="name" />
                                                    <YAxis />
                                                    <Legend />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="Actual Percentage"
                                                        // stackId="1"
                                                        stroke="#8884d8"
                                                        strokeWidth={3}
                                                        fillOpacity={0}
                                                        fill="#000000"
                                                    />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="Expected Percentage"
                                                        // stackId="1"
                                                        stroke="#82ca9d"
                                                        strokeWidth={3}
                                                        fillOpacity={0}
                                                        fill="#000000"
                                                    />
                                                    {/*<Bar*/}
                                                    {/*    dataKey="Requirements"*/}
                                                    {/*    fill="#82ca9d"*/}
                                                    {/*    barSize={10}*/}
                                                    {/*    onClick={handleBarClick}*/}
                                                    {/*/>*/}
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                </Flex>

                                <Flex direction={'row'} marginTop={30}>
                                    <Text minWidth={75} UNSAFE_className={'manabaseMenuBoxLabel'}>
                                        Card castability:
                                    </Text>
                                    <MenuTrigger>
                                        <ActionButton
                                            flexBasis={500}
                                            flexGrow={1}
                                            UNSAFE_className={'manabaseMenuBoxActionLabel'}
                                        >
                                            {deck?.cards?.[dropDownCardId]?.name}
                                        </ActionButton>
                                        <Menu
                                            items={cardIdList()}
                                            selectedKeys={dropDownCardId}
                                            onSelectionChange={(evn) => {
                                                setDropDownCardId(evn?.currentKey);
                                            }}
                                            selectionMode="single"
                                        >
                                            {(item) => (
                                                <Section items={item.children} title={item.name}>
                                                    {(item) => <Item>{item.name}</Item>}
                                                </Section>
                                            )}
                                        </Menu>
                                    </MenuTrigger>
                                </Flex>
                                <Flex direction="row" justifyContent={'space-around'}>
                                    <div
                                        style={{
                                            position: 'relative',
                                            width: '100%',
                                            maxWidth: '700px',
                                            paddingBottom: '250px',
                                            height: 200,
                                        }}
                                    >
                                        <div
                                            style={{
                                                position: 'absolute',
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                top: 0,
                                            }}
                                        >
                                            <ResponsiveContainer width="100%" height={400}>
                                                <AreaChart
                                                    // layout="vertical"
                                                    width={400}
                                                    height={400}
                                                    data={cardData}
                                                    // barGap={1}
                                                    // tick
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="name" />
                                                    <YAxis />
                                                    {/*<Legend />*/}
                                                    <Area
                                                        type="monotone"
                                                        dataKey="inHand"
                                                        // stackId="1"
                                                        stroke="#8884d8"
                                                        fill="#8884d8"
                                                    />
                                                    {/*<Bar*/}
                                                    {/*    dataKey="Requirements"*/}
                                                    {/*    fill="#82ca9d"*/}
                                                    {/*    barSize={10}*/}
                                                    {/*    onClick={handleBarClick}*/}
                                                    {/*/>*/}
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                </Flex>
                            </>
                        )}

                        <Flex direction="row" justifyContent={'space-around'}>
                            <div
                                style={{
                                    position: 'relative',
                                    width: '100%',
                                    maxWidth: '700px',
                                    paddingBottom: '250px',
                                    height: 200,
                                }}
                            >
                                <div
                                    style={{
                                        position: 'absolute',
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        top: 0,
                                    }}
                                >
                                    <ResponsiveContainer width="100%" height={400}>
                                        <BarChart
                                            layout="vertical"
                                            width={400}
                                            height={400}
                                            data={data}
                                            barGap={1}
                                            tick
                                        >
                                            <XAxis type="number" />
                                            <YAxis dataKey="name" type="category" width={60} tickLine={false} />
                                            <Legend />
                                            <Bar
                                                dataKey="Production"
                                                fill="#8884d8"
                                                barSize={20}
                                                onClick={handleBarClick}
                                            />
                                            <Bar
                                                dataKey="Requirements"
                                                fill="#82ca9d"
                                                barSize={10}
                                                onClick={handleBarClick}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </Flex>

                        <Flex direction="column" justifyContent="center" width={'100%'}>
                            <span className={'manabaseDetailsLabel'}>Details</span>
                            <div>
                                <span className={'manabaseDetailsName'}>
                                    Estimated number of mana producing lands required:&nbsp;
                                </span>
                                <span className={'manabaseDetailsValue'}>
                                    {deck?.details?.manabase?.details?.landQuantityEstimatedBaseline}
                                </span>
                            </div>
                            <div>
                                <span className={'manabaseDetailsSubDetailName'}>
                                    &nbsp;&nbsp;&nbsp;&nbsp;Actual mana producing lands quantity:&nbsp;
                                </span>
                                <span className={'manabaseDetailsSubDetailValue'}>
                                    {deck?.details?.manabase?.breakdown?.totalManaProducingLandCount}
                                </span>
                            </div>
                            <div>
                                <span className={'manabaseDetailsSubDetailName'}>
                                    &nbsp;&nbsp;&nbsp;&nbsp;Required land quantity offset:&nbsp;
                                </span>
                                <span className={'manabaseDetailsSubDetailValue'}>
                                    {parseInt(deck?.details?.manabase?.details?.landQuantityScoreOffset)}
                                </span>
                            </div>
                            <p />
                            <div>
                                <span className={'manabaseDetailsName'}>Base total CMC:&nbsp;</span>
                                <span className={'manabaseDetailsValue'}>
                                    {deck?.details?.manabase?.breakdown?.baseTotalCmc}
                                </span>
                            </div>
                            <div>
                                <span className={'manabaseDetailsSubDetailName'}>
                                    &nbsp;&nbsp;&nbsp;&nbsp;Generic mana reducers:&nbsp;
                                </span>
                                <span className={'manabaseDetailsSubDetailValue'}>
                                    {Object.keys(deck?.details?.manabase?.breakdown?.genericManaRucers)?.length || 0}
                                </span>
                            </div>
                            <div>
                                <span className={'manabaseDetailsSubDetailName'}>
                                    &nbsp;&nbsp;&nbsp;&nbsp;Number of reduced cost cards:&nbsp;
                                </span>
                                <span className={'manabaseDetailsSubDetailValue'}>
                                    {deck?.details?.manabase?.details?.totalReducedCostCards}
                                </span>
                            </div>
                            {/*<div>*/}
                            {/*    <span className={'manabaseDetailsSubDetailName'}>*/}
                            {/*        &nbsp;&nbsp;&nbsp;&nbsp;Total reduction:&nbsp;*/}
                            {/*    </span>*/}
                            {/*    <span className={'manabaseDetailsSubDetailValue'}>*/}
                            {/*        {deck?.details?.manabase?.breakdown?.genericManaReductionTotal}*/}
                            {/*    </span>*/}
                            {/*</div>*/}
                            {/*<div>*/}
                            {/*    <span className={'manabaseDetailsSubDetailName'}>*/}
                            {/*        &nbsp;&nbsp;&nbsp;&nbsp;Total CMC offset:&nbsp;*/}
                            {/*    </span>*/}
                            {/*    <span className={'manabaseDetailsSubDetailValue'}>*/}
                            {/*        {deck?.details?.manabase?.breakdown?.totalCmcOffset}*/}
                            {/*    </span>*/}
                            {/*</div>*/}
                            {/*<div>*/}
                            {/*    <span className={'manabaseDetailsSubDetailName'}>*/}
                            {/*        &nbsp;&nbsp;&nbsp;&nbsp;Adjusted total CMC:&nbsp;*/}
                            {/*    </span>*/}
                            {/*    <span className={'manabaseDetailsSubDetailValue'}>*/}
                            {/*        {deck?.details?.manabase?.breakdown?.totalCmc}*/}
                            {/*    </span>*/}
                            {/*</div>*/}
                            <p />
                            <p />
                            <Grid columns={repeat('auto-fit', 350)} gap={30} justifyContent="center">
                                {Object.keys(deck?.details?.manabase?.breakdown?.production?.breakdown)?.map((key) => (
                                    <div id={`__breakdown_inset_${key}`} className={'manabaseStatisticsPanel'}>
                                        <div className={'manabaseStatisticsHeader'}>
                                            Statistics for&nbsp;&nbsp;
                                            <span className={`mana medium s${key}`} />
                                        </div>
                                        <Flex direction={'column'} UNSAFE_className={'manabaseStatisticsPanelContent'}>
                                            <Flex direction={'row'}>
                                                <Flex direction={'column'}>
                                                    <div className={'manabaseStatisticsSubHeader'}>Requirements:</div>
                                                    <span>
                                                        <span className={'manabaseStatisticsDetailName'}>
                                                            Pip quantity:&nbsp;
                                                        </span>
                                                        <span className={'manabaseStatisticsDetailValue'}>
                                                            {
                                                                deck?.details?.manabase?.breakdown?.production
                                                                    ?.breakdown[key]?.requirements?.count
                                                            }
                                                        </span>
                                                    </span>
                                                    <span>
                                                        <span className={'manabaseStatisticsDetailName'}>
                                                            Average density:&nbsp;
                                                        </span>
                                                        <span className={'manabaseStatisticsDetailValue'}>
                                                            {
                                                                deck?.details?.manabase?.breakdown?.production
                                                                    ?.breakdown[key]?.requirements?.averageDensity
                                                            }
                                                        </span>
                                                    </span>
                                                </Flex>

                                                <Flex direction={'column'}>
                                                    <div className={'manabaseStatisticsSubHeader'}>Production:</div>
                                                    <div>
                                                        <span className={'manabaseStatisticsDetailName'}>
                                                            Total count:&nbsp;
                                                        </span>
                                                        <span className={'manabaseStatisticsDetailValue'}>
                                                            {
                                                                deck?.details?.manabase?.breakdown?.production
                                                                    ?.breakdown[key]?.production?.count
                                                            }
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className={'manabaseStatisticsDetailName'}>
                                                            Average produced:&nbsp;
                                                        </span>
                                                        <span className={'manabaseStatisticsDetailValue'}>
                                                            {
                                                                deck?.details?.manabase?.breakdown?.production
                                                                    ?.breakdown[key]?.production?.averageProduced
                                                            }
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className={'manabaseStatisticsDetailName'}>
                                                            Slow producer count:&nbsp;
                                                        </span>
                                                        <span className={'manabaseStatisticsDetailValue'}>
                                                            {
                                                                deck?.details?.manabase?.breakdown?.production
                                                                    ?.breakdown[key]?.production?.details?.slowQuantity
                                                            }
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className={'manabaseStatisticsDetailName'}>
                                                            Basic land count:&nbsp;
                                                        </span>
                                                        <span className={'manabaseStatisticsDetailValue'}>
                                                            {
                                                                deck?.details?.manabase?.breakdown?.production
                                                                    ?.breakdown[key]?.production?.details?.basicLands
                                                            }
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className={'manabaseStatisticsDetailName'}>
                                                            Fetchable land count:&nbsp;
                                                        </span>
                                                        <span className={'manabaseStatisticsDetailValue'}>
                                                            {
                                                                deck?.details?.manabase?.breakdown?.production
                                                                    ?.breakdown[key]?.production?.details
                                                                    ?.fetchableLands
                                                            }
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className={'manabaseStatisticsDetailName'}>
                                                            Fetch land count:&nbsp;
                                                        </span>
                                                        <span className={'manabaseStatisticsDetailValue'}>
                                                            {
                                                                deck?.details?.manabase?.breakdown?.production
                                                                    ?.breakdown[key]?.production?.details?.fetchesCount
                                                            }
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className={'manabaseStatisticsDetailName'}>
                                                            Basic fetch land count:&nbsp;
                                                        </span>
                                                        <span className={'manabaseStatisticsDetailValue'}>
                                                            {
                                                                deck?.details?.manabase?.breakdown?.production
                                                                    ?.breakdown[key]?.production?.details
                                                                    ?.basicFetchesCount
                                                            }
                                                        </span>
                                                    </div>
                                                    <span className={'manabaseStatisticsDetailName'}>Types:</span>
                                                    <Flex direction={'column'}>
                                                        {Object.keys(
                                                            deck?.details?.manabase?.breakdown?.production?.breakdown?.[
                                                                key
                                                            ]?.production?.types || []
                                                        )?.map((type) => (
                                                            <div>
                                                                <span className={'manabaseStatisticsDetailName'}>
                                                                    &nbsp; &nbsp; &nbsp; &nbsp;
                                                                    {type.replace(
                                                                        type.charAt(0),
                                                                        type.charAt(0).toUpperCase()
                                                                    )}
                                                                    &nbsp;
                                                                </span>
                                                                <span className={'manabaseStatisticsDetailValue'}>
                                                                    {
                                                                        deck?.details?.manabase?.breakdown?.production
                                                                            ?.breakdown[key]?.production?.types[type]
                                                                    }
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </Flex>
                                                </Flex>
                                            </Flex>
                                            {/*<Flex direction={'column'}>*/}
                                            {/*    <span className={'manabaseStatisticsPercentagesHeader'}>*/}
                                            {/*        Percentages relative to color identity:*/}
                                            {/*    </span>*/}
                                            {/*    <div>*/}
                                            {/*        <span className={'manabaseStatisticsDetailName'}>*/}
                                            {/*            Mana requirements:&nbsp;*/}
                                            {/*        </span>*/}
                                            {/*        <span className={'manabaseStatisticsDetailValue'}>*/}
                                            {/*            {parseInt(*/}
                                            {/*                parseFloat(*/}
                                            {/*                    deck?.details?.manabase?.breakdown?.production*/}
                                            {/*                        ?.breakdown[key]?.percentages?.requirements*/}
                                            {/*                ) * 100*/}
                                            {/*            )}*/}
                                            {/*            %*/}
                                            {/*        </span>*/}
                                            {/*    </div>*/}
                                            {/*    <div>*/}
                                            {/*        <span className={'manabaseStatisticsDetailName'}>*/}
                                            {/*            Mana production:&nbsp;*/}
                                            {/*        </span>*/}
                                            {/*        <span className={'manabaseStatisticsDetailValue'}>*/}
                                            {/*            {parseInt(*/}
                                            {/*                parseFloat(*/}
                                            {/*                    deck?.details?.manabase?.breakdown?.production*/}
                                            {/*                        ?.breakdown[key]?.percentages?.production*/}
                                            {/*                ) * 100*/}
                                            {/*            )}*/}
                                            {/*            %*/}
                                            {/*        </span>*/}
                                            {/*    </div>*/}
                                            {/*    <div>*/}
                                            {/*        <span className={'manabaseStatisticsDetailName'}>*/}
                                            {/*            Production to requirements ratio:&nbsp;*/}
                                            {/*        </span>*/}
                                            {/*        <span className={'manabaseStatisticsDetailValue'}>*/}
                                            {/*            {parseInt(*/}
                                            {/*                parseFloat(*/}
                                            {/*                    deck?.details?.manabase?.breakdown?.production*/}
                                            {/*                        ?.breakdown[key]?.percentages*/}
                                            {/*                        ?.productionToRequirementRatio*/}
                                            {/*                ) * 100*/}
                                            {/*            )}*/}
                                            {/*            %*/}
                                            {/*        </span>*/}
                                            {/*    </div>*/}
                                            {/*    <div>*/}
                                            {/*        <span className={'manabaseStatisticsDetailName'}>*/}
                                            {/*            Commander pip ratio:&nbsp;*/}
                                            {/*        </span>*/}
                                            {/*        <span className={'manabaseStatisticsDetailValue'}>*/}
                                            {/*            {parseInt(*/}
                                            {/*                parseFloat(*/}
                                            {/*                    deck?.details?.manabase?.breakdown?.production*/}
                                            {/*                        ?.breakdown[key]?.percentages*/}
                                            {/*                        ?.commanderPipRatio*/}
                                            {/*                ) * 100*/}
                                            {/*            )}*/}
                                            {/*            %*/}
                                            {/*        </span>*/}
                                            {/*    </div>*/}
                                            {/*</Flex>*/}
                                        </Flex>
                                    </div>
                                ))}
                            </Grid>
                            <p />
                            <p />
                            <p />
                            <Flex marginTop={20} direction={'column'}>
                                <DetailsExpansionSubpane
                                    title={'Why does this look different than Moxfield, Archidekt, etc?'}
                                    isOpen={false}
                                >
                                    <Flex direction="column" marginBottom={'20px'}>
                                        <span>
                                            We display mana percentages differently than a lot of other sites, because
                                            we use the average density of pips in each color per card as a weighted
                                            average. Typically this is presented in more of a total pips / total
                                            production type of way.
                                            <p />
                                            The reason that we calculate it this way is because density of pips in cards
                                            dictates the boardstate required to play them.
                                            <p />
                                            For example: 4 cards with <span className={`mana medium sg`} />
                                            <span className={`mana medium sg`} />
                                            <span className={`mana medium sg`} /> in the mana cost requires a different
                                            board state than 12 cards with only <span className={`mana medium sg`} /> in
                                            their casting cost.
                                        </span>
                                    </Flex>
                                </DetailsExpansionSubpane>
                                <DetailsExpansionSubpane title={'How are land requirements calculated?'} isOpen={false}>
                                    <Flex direction="column" marginBottom={'20px'}>
                                        First we determine a baseline quantity of lands that would be able to support
                                        the average cmc of cards in your deck without ramp, rocks, dorks, or rituals.
                                        Basically, just lands for mana.
                                        <p />
                                        We then look at the relative quality of rocks, dorks, and rituals in your deck,
                                        and use that as an offset to the estimated number of lands needed.
                                    </Flex>
                                </DetailsExpansionSubpane>
                                <DetailsExpansionSubpane title={'How are mana producing cards scored?'} isOpen={false}>
                                    <Flex direction="column" marginBottom={'20px'}>
                                        {/*<b>HEADER:</b>*/}
                                        We look at the average density of each color pip in each card in your deck. We
                                        then compare that to the production of that color relative to your manabase.
                                        This results in a weighted average of needs versus production.
                                        <p />
                                        When we look assess the value of individual mana producing cards in your deck,
                                        the score is determined based on the value of the mana it produces relative to
                                        the actual needs of your deck.
                                        <p />
                                        In short, Underground Sea in one deck will not necessarily be the same score as
                                        Underground Sea in another deck.
                                        <p />
                                        After that, we look at the following:
                                        <ul>
                                            <li>Does it ETB tapped</li>
                                            <li>Does it ETB tapped conditionally (eg Watery Grave)</li>
                                            <li>Is it a MDFC or transform card</li>
                                            <li>What is its CMC</li>
                                            <li>How much of each mana type does it produce</li>
                                            <ul>
                                                <li>
                                                    What is the weighted value of each of these color types (including
                                                    colorless)
                                                </li>
                                            </ul>
                                            <li>
                                                For lands we also look at
                                                <ul>
                                                    <li>Is it basic</li>
                                                    <li>Is it fetchable</li>
                                                    <ul>
                                                        <li>How many fetches do you use that can fetch it?</li>
                                                    </ul>
                                                </ul>
                                            </li>
                                        </ul>
                                    </Flex>
                                </DetailsExpansionSubpane>
                            </Flex>
                        </Flex>
                    </Flex>
                </Flex>
            </Flex>
        </BaseDeckPane>
    );
}
